// Mock data for the CAM care search tab — stand-ins for the search API,
// shortlist store and provider records. The member-side mirror of the same
// world lives in karehero-app/care/shortlists/fixtures.ts, kept in sync by
// hand so the two prototypes tell one story.

export type Provider = {
  name: string;
  address: string;
  distance: string;
  tags: string[];
  price: string;
  phone: string;
  status: string;
};

export const RESULTS: Provider[] = [
  {
    name: 'Oakwood care home',
    address: '14 Brae Street, Edinburgh EH3 6HR',
    distance: '0.8 miles',
    tags: ['Residential', 'Dementia', 'Rating Good'],
    price: '£1,250/week',
    phone: '0131 555 0299',
    status: 'Available',
  },
  {
    name: 'Riverside lodge',
    address: '2 Mill Lane, Edinburgh EH4 1AB',
    distance: '1.4 miles',
    tags: ['Nursing', 'Dementia', 'Rating Good'],
    price: '£1,320/week',
    phone: '0131 555 1190',
    status: 'Available',
  },
  {
    name: 'Cherry Tree house',
    address: '88 Orchard Road, Edinburgh EH5 2QP',
    distance: '2.1 miles',
    tags: ['Residential', 'Rating Outstanding'],
    price: '£1,480/week',
    phone: '0131 555 7723',
    status: 'Waitlist',
  },
  {
    name: 'Bramble court',
    address: '5 Hillview Terrace, Edinburgh EH6 8LN',
    distance: '3.0 miles',
    tags: ['Residential', 'Dementia'],
    price: '£1,180/week',
    phone: '0131 555 4401',
    status: 'Available',
  },
  // On a shortlist but missing from the results until now, which meant the one
  // provider a CAM could not open the full page for was the one already on their
  // list. Unavailable here, matching the no-capacity flag the paywall tiering
  // gives it — the two views of a provider have to agree.
  {
    name: 'Hillcrest manor',
    address: '31 Kirk Brae, Edinburgh EH7 4TG',
    distance: '4.2 miles',
    tags: ['Nursing', 'Palliative'],
    price: '£1,540/week',
    phone: '0131 555 8814',
    status: 'Unavailable',
  },
];

export type ShortlistItem = {
  name: string;
  address: string;
  phone: string;
  status?: string;
  note?: string;
  editingInitial?: boolean;
  // Dismissed by the CAM: hidden from the care circle, kept for the audit
  // trail. Distinct from the 'Unavailable' status, which stays visible.
  dismissed?: { reason: string; detail?: string };
};

export type Shortlist = {
  id: string;
  name: string;
  status: 'draft' | 'published';
  // Edited since last publish — gates the "Publish changes" button.
  dirty?: boolean;
  items: ShortlistItem[];
};

// Seed items for the pre-populated demo scenarios.
export const SEED_ITEMS: ShortlistItem[] = [
  {
    name: 'Oakwood care home',
    address: '14 Brae Street, Edinburgh EH3 6HR',
    phone: '0131 555 0299',
    status: 'Contacted',
  },
  {
    name: 'Riverside lodge',
    address: '2 Mill Lane, Edinburgh EH4 1AB',
    phone: '0131 555 1190',
    status: 'Contacted',
    note: 'Receptionist is unsure if the space available has already been reserved and will get back to me before the end of the week.',
    editingInitial: true,
  },
  {
    name: 'Cherry Tree house',
    address: '88 Orchard Road, Edinburgh EH5 2QP',
    phone: '0131 555 7723',
    status: 'Contacted',
    note: 'We need three months of recent statements to complete the funding picture. Please upload these as a PDF. I’ve attached an example of what they should look like.',
  },
  {
    name: 'Bramble court',
    address: '5 Hillview Terrace, Edinburgh EH6 8LN',
    phone: '0131 555 4401',
  },
  {
    name: 'Hillcrest manor',
    address: '41 Crest Road, Edinburgh EH7 4DN',
    phone: '0131 555 8830',
    status: 'Unavailable',
    dismissed: {
      reason: 'No availability',
      detail: 'No beds until autumn at the earliest.',
    },
  },
];

// ─── care secured elsewhere (declared on the member side) ────────────────────

// The member telling us they arranged care outside the shortlisted homes, as the
// CAM receives it: who declared it and when. Sits beside the shortlists rather
// than on one of them — one care circle, one declaration.
export type SecuredElsewhere = {
  // The carer who declared it (the circle's primary carer in these fixtures).
  by: string;
  date: string;
};

export const SECURED_ELSEWHERE_DECLARATION: SecuredElsewhere = {
  by: 'Sophie',
  date: '18 Jul 2026',
};

// ─── global provider notes (CAM-only) ────────────────────────────────────────

export type ProviderNote = {
  id: string;
  author: string;
  date: string;
  text: string;
};

// The CAM using the prototype. Only their own notes are editable.
export const CURRENT_CAM = 'You';

export const SEED_NOTES: ProviderNote[] = [
  {
    id: 'n1',
    author: 'Priya Patel',
    date: '28 May 2026',
    text: 'Very responsive manager - called back within the hour. Two rooms free on the ground floor.',
  },
  {
    id: 'n2',
    author: 'James Okafor',
    date: '21 May 2026',
    text: 'Visited with a family in April. Gardens are lovely but parking is tight for visitors.',
  },
];

// ─── mock map ────────────────────────────────────────────────────────────────

// Hardcoded pin positions (percentages) for the mock map view.
export const MAP_PINS: Record<string, { x: number; y: number }> = {
  'Oakwood care home': { x: 44, y: 30 },
  'Riverside lodge': { x: 66, y: 58 },
  'Cherry Tree house': { x: 28, y: 66 },
  'Bramble court': { x: 78, y: 26 },
};

export const HOME_PIN = { x: 50, y: 46 };

// ─── provider detail ─────────────────────────────────────────────────────────

// One detail record stands in for every provider (the prototype reuses it,
// overlaying the tapped provider's name, address, price and phone).
export const PROVIDER_DETAIL = {
  rating: 'Good',
  fallbackPrice: '£1,250/week',
  beds: '32',
  fallbackAvailability: 'Available',
  manager: 'Sarah Mbeki',
  visiting: 'Open visiting',
  fallbackPhone: '0131 555 0299',
  website: 'oakwoodcare.co.uk',
  careTypes: ['Residential', 'Nursing', 'Dementia', 'Respite'],
  features: [
    'En-suite rooms',
    'Landscaped gardens',
    'On-site physiotherapy',
    'Pet friendly',
  ],
  ownerQuote:
    '“We’ve cared for our local community for over 20 years, and every member of our team treats residents like family.”',
  ownerDescription:
    'Oakwood is a purpose-built home offering residential, nursing and specialist dementia care across two landscaped acres. Our on-site clinical team supports complex needs, and a varied activities programme keeps residents engaged.',
  respitePrice: '£1,400/week',
  fundingAccepted: 'Self-funded · Local authority · NHS',
  fundingNote:
    'Local authority funding can apply below £35,000 in savings (Scotland). The home confirms funding on assessment.',
  regulatorLine: 'CQC overall · last inspected Jan 2026',
  inspection: [
    { area: 'Safe', rating: 'Good' },
    { area: 'Effective', rating: 'Good' },
    { area: 'Caring', rating: 'Outstanding' },
    { area: 'Responsive', rating: 'Good' },
    { area: 'Well-led', rating: 'Good' },
  ],
};
